// Sheet detail — one worker's week. GET /api/timesheets/:id.
//
// Typed hours are authoritative, the clock is evidence (REBUILD_API.md §0):
// a line whose hours differ from what its clock said is marked ADJUSTED, with
// the reason and the clock figure underneath — the owner's required "show
// that it was manually updated" signal. Editing sends the FULL entries array
// (the server deletes lines it is not sent). Approval is the only freeze.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  JobCard, SectionLabel, Sheet, Notice, Skeleton, FwButton, FwField, FwInput, FwSelect, FwTextarea,
  Pill, formatClock, usePhoneLayout,
} from '../../../ui/fw';
import { useCan } from '../../../hooks/usePermission';
import { get, put, post, describeError } from '../../shared/http';
import { formatDateTime } from '../../../utils/timezone';
import { usePageChrome } from '../../shell/chrome';
import { dayShort, dayLong, weekOfLabel, hoursClock, moneyText, entriesForPut, reasonRequired, serverSentence, storedDay, storedTime, sheetPill } from '../lib/format';
import TimesheetGrid from './TimesheetGrid';
import { linePriceLabel, isAmountLine, lineRate, lineRateLabel } from '../../../utils/timesheetPay';
import { JobPeek, jobFromRow } from '../../shared/JobPeek';
import {
  weekDates, jobChoices, newLineError, entriesWithNewLine, entriesWithout,
  PAY_CATEGORIES, payCategory, payCategoryLabel,
} from './sheetLines';
import { HoursField } from '../../shared/HoursField';

const list = (a) => (Array.isArray(a) ? a : []);

/**
 * How to fix the hours the approve question names, for THIS reader: only the
 * doors they have. A line's rate needs jobs.set_pay; a person's rate is on
 * Crew, behind the rate permission a manager does not hold — the same split
 * as the no-rate warning above the grid.
 */
export function unpricedFix(canPrice, ratesVisible, who) {
  const person = ratesVisible ? `set ${who}'s rate under Crew` : `ask an admin to set ${who}'s rate`;
  const said = canPrice ? `Edit the line to give it a rate, or ${person}.` : `${person}.`;
  return `${said.charAt(0).toUpperCase()}${said.slice(1)} The paystub is written when the week is approved.`;
}

/**
 * The flat figure THIS LINE carries, as a NUMBER, or null.
 *
 * `lineAmountLabel` in utils/timesheetPay returns it FORMATTED ("$50.00"),
 * which is right beside a figure and wrong inside `<input type="number">` — a
 * dollar sign there is an invalid value and the box silently comes up empty.
 *
 * ── `line.pay_amount`, NOT resolveEntryPay ────────────────────────────────
 *
 * The first version read the resolver, so it fell back to `job_pay_amount` and
 * pre-filled the box with the JOB's price. Raised in review on #686, and it was
 * a payroll-zeroing bug rather than a cosmetic one. Every link verified in the
 * source:
 *
 *   · resolveEntryPay (timesheetPay.js:44) falls back to job_pay_amount, so a
 *     line on a FIXED-PRICE JOB with hours 0 resolves fixed — isAmountLine
 *     says true for it, and hours 0 is a normal correction (HOUR_CHOICES
 *     includes 0 and save() accepts it).
 *   · saveAmount PUTs pay_amount and no pay_type.
 *   · hoursModel.js:296 keeps the STORED type, and every job line is stored
 *     'hourly' — the clock INSERT hardcodes it (hoursModel.js:210).
 *   · paystubCalc.js:228 — once the ENTRY has an amount, the ENTRY's type
 *     governs. So the line flips fixed -> hourly.
 *   · paystubCalc.js:361 — hourly pays hours * rate = 0 * 500 = $0.00.
 *
 * A $500 flat job line would have started paying nothing, silently, with the
 * only visible change being linePriceLabel reading "$500.00/hr". Second order,
 * same root: writing pay_amount onto the entry makes fixedPayGroup return null
 * (paystubCalc.js:282), so the spend-once rule stops protecting a multi-day
 * fixed job.
 *
 * Reading the line's own column cannot do any of that: there is nothing to
 * offer unless the entry already carries the figure, so the editor can only
 * ever rewrite a number this sheet already owns.
 */
function amountOfLine(line) {
  if (!line) return null;
  const own = parseFloat(line.pay_amount || 0);
  return own > 0 ? own : null;
}
const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || 'The worker';

/**
 * What the line is FOR. The job, not the address.
 *
 * Owner, 2026-09-16: "show Unit/apt name with label so we can tell exactly
 * what's a title and what's the unit/apt name."
 *
 * This read `property_name || job_title || custom_description`, so the big
 * text on every line was the UNIT — "Hillcrest" — and the work it was for was
 * a fragment of the grey meta line underneath. On the screen where somebody
 * decides whether to pay for work, the work was the small print. Inverted
 * now; the unit is still shown, with a label, by lineUnit below.
 */
function lineTitle(e) {
  return e.job_title || e.custom_description || e.property_name || 'Line';
}

/** The unit or apartment, SAID to be one. Null when there is none. */
function lineUnit(e) {
  if (!e.property_name) return null;
  return (
    <span className="dp-line__unit" data-line-unit>
      <span className="dp-line__unit-label">Unit</span>
      {' '}{e.property_name}
    </span>
  );
}

/** Enough of the description to tell two jobs apart, never a paragraph. */
const DESCRIPTION_MAX = 120;
function lineDescription(e) {
  const d = String(e.job_description || '').trim();
  if (!d) return null;
  const shown = d.length > DESCRIPTION_MAX ? `${d.slice(0, DESCRIPTION_MAX - 1).trimEnd()}…` : d;
  return <span className="dp-line__desc" data-line-description>{shown}</span>;
}

/**
 * When the job was raised and when it was finished.
 *
 * Owner, 2026-09-16: "Show job creation and completion date/time." Labelled
 * both times, and a half that does not exist is not drawn — an empty
 * "Completed —" reads as data, which is worse than the absence.
 */
function lineWhen(e) {
  if (!e.job_created_at && !e.job_completed_at) return null;
  const when = (v) => `${storedDay(v)} ${storedTime(v)}`.trim();
  return (
    <div className="dp-line__when" data-line-when>
      {e.job_created_at ? (
        <span className="dp-line__when-part">
          <span className="dp-line__when-label">Created</span>{' '}{when(e.job_created_at)}
        </span>
      ) : null}
      {e.job_created_at && e.job_completed_at ? <span className="dp-line__sep" aria-hidden="true"> · </span> : null}
      {e.job_completed_at ? (
        <span className="dp-line__when-part">
          <span className="dp-line__when-label">Completed</span>{' '}{when(e.job_completed_at)}
        </span>
      ) : null}
    </div>
  );
}
/**
 * The line's context, its hours, and what it is PRICED at (parity §5).
 *
 * `job_pay_type`/`job_pay_amount` have ridden in on every line since
 * hoursModel.sheetLines was written and nothing drew them, so the person
 * turning these hours into payroll could not tell a $500 flat line from an
 * hourly one — which is the difference between eight hours costing $500 and
 * eight hours costing eight times a rate.
 *
 * linePriceLabel resolves through the mirror of server/utils/paystubCalc.js
 * and FORMATS. It multiplies nothing, totals nothing, and returns null when
 * the only rate left is the worker's profile rate — which this response does
 * not carry, so there is nothing honest to print. CLAUDE.md rule 4.
 *
 * ── And a line that is an AMOUNT has no hours to show ──────────────────────
 *
 * Added 2026-09-10, the owner: "What if I want to adjust the timesheet to
 * show reinbursement, bonus, etc or other adjustment that has a set amount
 * and not hourly."
 *
 * Such a row is stored with `hours: 0` and paid its `pay_amount` flat
 * (paystubCalc.computeFigures), so `hoursClock(0)` printed "0:00" in the slot
 * an approver reads a line's value out of — a $50 reimbursement rendering as
 * a line worth nothing, on the screen that turns it into pay. isAmountLine
 * asks the same resolver the server pays through, so the slot is dropped on
 * exactly the rows where it is meaningless, and never on a fixed-price job
 * that was actually worked (those hours are the reconciliation against the
 * clock).
 *
 * ── …and what it is WORTH, which is a different question ───────────────────
 *
 * Added 2026-09-10 with the grid. `linePriceLabel` says what a line is priced
 * AT — "$32.50/hr", "Fixed $500.00" — and could never say what it comes to,
 * because that needs `users.hourly_rate` and no timesheet response carried
 * one. The note it used to end on said exactly that: "the week's total is
 * still not shown … computing it here would be the duplicated arithmetic".
 *
 * The response carries the figure now (server/utils/timesheetGrid.js, off
 * paystubCalc.computeFigures), so `value` is that figure passed in and
 * printed. It is still not computed here, and must never be: hours × a rate
 * on a screen is #202, a number agreeing with neither the truth nor the
 * paystub.
 *
 * `value` is null for a reader without `workers.read_sensitive`, and the slot
 * simply is not drawn — never "$0.00", which is a wrong number where a
 * withheld one belongs.
 */
/**
 * Join the parts that exist with a visible separator.
 *
 * Built as a list rather than concatenated, because every part here is
 * conditional and the first version simply rendered them adjacent — which
 * printed "Unit HillcrestSmithWeekly mow, edge and blow…Created Tue 1". A
 * separator that is only correct when every part happens to be present is not
 * a separator.
 */
function joinParts(parts) {
  const present = parts.filter(Boolean);
  return present.map((node, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={i}>
      {i > 0 ? <span className="dp-line__sep" aria-hidden="true"> · </span> : null}
      {node}
    </React.Fragment>
  ));
}

/**
 * What KIND of payment an amount line is, said on the line.
 *
 * Owner, 2026-09-16: the amount lines should "clearly get marked to make
 * things easier for the employee accountant". The description already says
 * what the money was for; this says which bucket it belongs in, in the same
 * word every time, so an approver scanning a week can tell a repayment from
 * wages without reading five sentences.
 *
 * Only on amount lines, and only when there IS one: every line written before
 * migration 0008 has a null category, and an empty chip reads as data.
 * `payCategoryLabel` falls back to the raw key rather than rendering blank, so
 * a value this version does not know still shows as something.
 */
function lineCategory(e) {
  if (!isAmountLine(e)) return null;
  const label = payCategoryLabel(e.pay_category);
  if (!label) return null;
  return (
    <span className="dp-line__category" data-line-category={e.pay_category}>
      {label}
    </span>
  );
}

/**
 * What a line is paid AT, and where that rate came from.
 *
 * Owner, 2026-09-28: "When admin creates timesheets, rate displays as
 * 0.00/hr. There rate should come from employee, job overrides rate for that
 * job and admin can override at the timesheet."
 *
 * This read `linePriceLabel`, which names the line's own rate or the job's
 * and goes silent on the employee's — so a line paid at Maria's own $25 said
 * nothing about a rate at all, and a line with no rate anywhere, which pays
 * $0.00, looked exactly like it. `lineRateLabel` names all three sources and
 * the none, off `grid.rate`, which the server sends only to a reader who may
 * see rates (null otherwise, and then only what the line itself carries is
 * named). An AMOUNT line keeps its plain "Fixed $50.00": it is not a rate.
 */
function rateOf(e, workerRate, who) {
  if (isAmountLine(e)) {
    const price = linePriceLabel(e);
    return price ? { text: price, from: null, none: false } : null;
  }
  return lineRateLabel(e, workerRate, who);
}

function lineMeta(e, value, rate) {
  return (
    <>
      {joinParts([
        lineCategory(e),
        lineUnit(e),
        e.client_name ? <span className="dp-line__client">{e.client_name}</span> : null,
        lineDescription(e),
      ])}
      {' '}
      {isAmountLine(e) ? null : <span className="fw-num">{hoursClock(e.hours)}</span>}
      {rate ? (
        <span className={rate.none ? 'dp-line__price dp-line__price--none' : 'dp-line__price'} data-line-rate={e.id} data-rate-none={rate.none ? 'true' : 'false'}>
          {rate.text}
          {rate.from ? <span className="dp-line__rate-from"> · {rate.from}</span> : null}
        </span>
      ) : null}
      {value ? <span className="dp-line__value fw-num" data-line-value={e.id}>{value}</span> : null}
    </>
  );
}

/**
 * The provenance signal on a line, or nothing.
 *
 *   Amount   — paid a flat sum, no hours (a reimbursement, a bonus)
 *   Adjusted — built from the clock, hours changed since (hoursModel.sourceFor)
 *   Typed    — NO clock record behind it at all
 *
 * Amount comes FIRST and replaces Typed, which every such line would also
 * qualify for (an amount line has no clock behind it by construction). The
 * two modes have to be unmistakable on a payroll screen: "50" meant as
 * dollars sitting where hours are read, or the reverse, is a pay error that
 * looks like a plausible number — and CLAUDE.md names that the worst outcome
 * available here. "Typed" would have said the true but useless half.
 *
 * "Typed" was added 2026-09-07 alongside "Add a line by hand". Before that
 * every line on this screen had come from the clock, so "no clock behind it"
 * was not a state the card had to describe. It is now: a hand-added line and a
 * clock line render identically, and the whole point of this screen is that an
 * approver can see which hours have evidence under them. Legacy lines that
 * predate the clock link land here too, which is accurate — they were typed.
 *
 * ── `badges`, NOT `trailing` — measured, 2026-09-10 ────────────────────────
 *
 * These went in JobCard's `trailing` slot until a browser run at 320px
 * reported the title "Fuel reimbursement (ui-check)" over FIVE LINES in 82px.
 * `.fw-jcard__trailing` is `flex: none`, so an uppercased, letter-spaced pill
 * takes its full natural width out of a 320px row and the title gets what is
 * left — and `.fw-jcard__text` already carries `min-width: 0`, so nothing
 * there can give it back.
 *
 * This is the SAME defect, in the same component, that WeekJobs below records
 * finding on 2026-09-09 with "NOT ON THE SHEET". It was fixed there and not
 * here, because nothing had driven this list in a browser with a long enough
 * title — "Adjusted" and "Typed" on a two-word job name fit. The amount line
 * made it visible; it was always true.
 *
 * `badges` is the slot the component provides for exactly this
 * (docs/APP_REBUILD.md §3.3): it sits INSIDE the text column and wraps, so
 * the signal keeps its words and stops competing with the name of the thing
 * it describes.
 */
function lineBadges(e) {
  if (isAmountLine(e)) return [{ key: 'amount', label: 'Amount', tone: 'sched' }];
  if (e.adjusted) return [{ key: 'adjusted', label: 'Adjusted', tone: 'warn' }];
  if (e.clock_hours == null) return [{ key: 'typed', label: 'Typed', tone: 'neutral' }];
  return [];
}

/**
 * When a change was made, in the reader's own zone, or '' if it cannot be read.
 *
 * `at` is a stored SQLite timestamp ('2026-09-08 14:03:11') and therefore UTC
 * with no marker on it — `new Date()` on that string reads it as LOCAL and is
 * four or five hours out in Connecticut. utils/timezone.formatDateTime goes
 * through parseUTCDate, which is the one place in the client that knows this.
 */
function changedAt(at) {
  if (!at) return '';
  try { return formatDateTime(at) || ''; } catch (_e) { return ''; }
}

/**
 * Every recorded change to this line's hours — collapsed until asked for.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * The owner, 2026-09-08: "there should also history of changes."
 *
 * `adjustment_reason`, `adjusted_by` and `adjusted_at` are SINGLE COLUMNS on
 * the line, so each adjustment overwrote the last. A line taken 8 → 6.5
 * ("timer ran through lunch") → 5.75 ("Marcus confirmed") showed only 5.75 and
 * "Marcus confirmed" — and the note above this, which is that single column,
 * was the entire answer to "how did this figure get here" on a payroll
 * dispute. The server records each change now and hands them back on the line
 * as `history`, oldest first (server/routes/timesheets.js).
 *
 * ── Why collapsed, and why it is not on the edit sheet ─────────────────────
 *
 * A line nobody has ever adjusted has an empty history and renders NOTHING —
 * no heading, no empty state, no control. A line that does have one costs a
 * single short label until somebody wants it, so a line adjusted six times
 * cannot bury the five lines under it.
 *
 * It is rendered on the line itself rather than inside EditLine because the
 * edit sheet does not open on an APPROVED timesheet (`onClick` is undefined
 * there) and an approved sheet is exactly when somebody asks how the figure
 * was arrived at. It also renders independently of `e.adjusted`: hours moved
 * away from the clock and then back again leave a line that is no longer
 * "adjusted" and still has a history worth reading.
 *
 * Figures are `hoursClock`, matching the card above it and the Approve button
 * below, so nothing on this screen has to be translated before it is compared.
 */
function LineHistory({ line }) {
  const changes = list(line && line.history);
  const [open, setOpen] = useState(false);
  if (!changes.length) return null;
  return (
    <div className="dp-line__note">
      <FwButton
        variant="quiet"
        icon={open ? 'chevronUp' : 'chevronDown'}
        aria-expanded={open ? 'true' : 'false'}
        data-history-toggle={String(line.id)}
        onClick={() => setOpen((v) => !v)}
      >
        {changes.length === 1 ? '1 change' : `${changes.length} changes`}
      </FwButton>
      {open ? changes.map((h, i) => (
        <div key={h.id != null ? h.id : i} data-history-row className="dp-line__note-clock">
          <span className="fw-num">{hoursClock(h.from_hours)}</span>
          {' → '}
          <span className="fw-num">{hoursClock(h.to_hours)}</span>
          {' · '}
          {h.by || 'someone no longer on the roster'}
          {changedAt(h.at) ? ` · ${changedAt(h.at)}` : ''}
          {' — '}
          {h.reason || 'no reason given'}
        </div>
      )) : null}
    </div>
  );
}

/**
 * Jobs this week — and which of them made it onto the sheet.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Restored 2026-09-07. The owner: "CAN'T TELL WHAT JOBS WERE IN THE WEEK AND
 * WHICH ONES MADE IT TO THE TIMESHEET."
 *
 * The classic Timesheets.js was 1,285 lines and carried an "Actual Tracked
 * Time This Week" table under the hint "Compare submitted hours against actual
 * time tracking data". The rebuilt screen is 217 lines and listed only the
 * sheet's own entries, so the one question an approver has to answer — is
 * anything MISSING from this week — had no answer on the screen. A manager saw
 * four lines totalling 15:30 and a green APPROVE button, with no hint that the
 * clock held 20:30.
 *
 * ── Where the data comes from, and where it must NOT ───────────────────────
 *
 * `sheet.week_jobs`, off GET /api/timesheets/:id, which is derived from the
 * worker's TIME ENTRIES — the same source hoursModel.clockLinesForWeek uses to
 * build the sheet.
 *
 * The first draft of this panel called GET /api/timesheets/week-jobs instead.
 * That endpoint filters `WHERE j.user_id = ?`, the LEAD pointer, so on a crew
 * job somebody else leads it silently omits the job. Measured on a week with 5
 * hours missing from the sheet, it summed to exactly the sheet's own total —
 * a PERFECT reconciliation while somebody's hours went unpaid. A false
 * all-clear on payroll is worse than showing nothing, so this reads the field
 * that cannot disagree with the builder rather than the one that looked right.
 *
 * Read-only. It computes no pay and changes no hours (CLAUDE.md rule 4).
 */
function WeekJobs({ sheet, entries }) {
  const jobs = list(sheet && sheet.week_jobs);
  const onSheet = new Set(
    list(entries).map((e) => (e.job_id == null ? null : String(e.job_id))).filter(Boolean),
  );
  const missing = jobs.filter((j) => !onSheet.has(String(j.id)) && Number(j.week_hours) > 0);
  const clockTotal = jobs.reduce((sum, j) => sum + (Number(j.week_hours) || 0), 0);
  const sheetTotal = Number(sheet && sheet.total_hours) || 0;

  return (
    <>
      <SectionLabel>Jobs this week · {jobs.length}</SectionLabel>
      {/* The gap, next to the button that commits it. A total the approver has
          to compute by eye from two lists is a total nobody computes. */}
      <div className="dp-quiet">
        {/* hoursClock, not formatHours: the Approve button below reads
            "Approve · 16:15" and a total in a different notation two inches
            above it is a total somebody has to translate before comparing. */}
        Clock <span className="fw-num">{hoursClock(clockTotal)}</span>
        {' · '}Sheet <span className="fw-num">{hoursClock(sheetTotal)}</span>
      </div>
      {missing.length ? (
        <Notice tone="warn">
          {missing.length === 1
            ? '1 job has clock time this week and no line on this sheet.'
            : `${missing.length} jobs have clock time this week and no line on this sheet.`}
          {' '}&ldquo;Add clock time for this week&rdquo; above will pick them up.
        </Notice>
      ) : null}
      {jobs.length === 0 ? (
        <div className="dp-quiet">No clock time in this week.</div>
      ) : null}
      <div className="dp-cards">
        {jobs.map((j) => {
          const included = onSheet.has(String(j.id));
          return (
            <JobCard
              key={j.id}
              time={hoursClock(j.week_hours)}
              title={j.title || `Job #${j.id}`}
              meta={[
                j.status,
                j.session_count ? `${j.session_count} ${j.session_count === 1 ? 'session' : 'sessions'}` : null,
              ].filter(Boolean).join(' · ')}
              tone={included ? 'neutral' : 'warn'}
              /* ── `badges`, NOT `trailing` — measured, 2026-09-09 ─────────
                 The first ui-check scenario to drive this screen reported the
                 job title rendering over SEVEN LINES in 59px at 320px. The
                 cause is in JobCard.css and is the classic flex starvation:
                 `.fw-jcard__trailing` is `flex: none`, so "NOT ON THE SHEET"
                 — uppercased and letter-spaced by Pill — took its full
                 natural width out of a 320px row and the title got what was
                 left. `.fw-jcard__text` already carries `min-width: 0`, so
                 nothing there could give it back.
                 `badges` is the slot the component provides for exactly this
                 (docs/APP_REBUILD.md §3.3): it sits INSIDE the text column
                 and wraps, so the signal keeps its words and stops competing
                 with the name of the job it describes. No information is
                 lost, and the tone still carries the colour. */
              badges={[included
                ? { key: 'on', label: 'On the sheet', tone: 'ok' }
                : { key: 'off', label: 'Not on the sheet', tone: 'warn' }]}
            />
          );
        })}
      </div>
    </>
  );
}

/**
 * The clock evidence under the week — every session, collapsed until asked for.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * docs/PARITY_2026-09-08.md §15: "The approver cannot see the clock evidence
 * for the week they are approving." The classic Timesheets.js had a panel
 * headed "Actual Tracked Time This Week" and a modal listing the individual
 * clock entries; the rebuilt screen listed the sheet's own lines and nothing
 * behind them.
 *
 * `GET /api/timesheets/:id/worker-jobs` (server/routes/timesheets.js:1018)
 * has answered this the whole time — for the sheet's exact UTC window, every
 * `time_entries` row the worker clocked — and had NO CALLER anywhere under
 * client/src. Nothing new is computed on the server and nothing is computed
 * here.
 *
 * ── Why it is the SESSION level and not another total ──────────────────────
 *
 * WeekJobs above already answers "is a job missing from this sheet", off
 * `week_jobs`, and that reconciliation is the load-bearing one — it must keep
 * reading the field that cannot disagree with the sheet builder. This panel
 * answers the different question an approver asks about a figure they are
 * about to turn into pay: WHEN was this worked. 8.25 hours on a Monday is one
 * shift or it is three, and those are not the same week to sign off.
 *
 * So this deliberately does NOT total anything. Adding up `duration` here
 * would produce a third hours figure on a screen that already carries two
 * (Clock and Sheet) derived from a different query — and a third number that
 * disagrees with either by a rounding step is exactly the "plausible wrong
 * number" CLAUDE.md calls the worst outcome available here.
 *
 * ── What is display and what would be arithmetic ───────────────────────────
 *
 * `duration` is the server's own integer of seconds; `formatClock` renders it
 * as H:MM. That is a unit format, not a calculation: no rate, no sum, no
 * rounding of hours. No pay figure appears on this panel at all.
 *
 * Lazily fetched. server.js:222 rate-limits to 1000 requests / 15 minutes and
 * this screen is opened once per worker per week; an always-on second query
 * would spend a request on every review for a panel most reviews do not open.
 */
function ClockSessions({ sheetId }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['timesheet', sheetId, 'clock-sessions'],
    queryFn: () => get(`/timesheets/${sheetId}/worker-jobs`),
    enabled: open,
    staleTime: 60000,
  });
  const sessions = list(q.data && q.data.time_entries);

  return (
    <div className="dp-sessions">
      <FwButton
        variant="quiet"
        icon={open ? 'chevronUp' : 'chevronDown'}
        aria-expanded={open ? 'true' : 'false'}
        data-sessions-toggle="true"
        onClick={() => setOpen((v) => !v)}
      >
        Clock sessions
      </FwButton>
      {open ? (
        <>
          {q.isPending ? <Skeleton lines={3} /> : null}
          {/* Names what failed FIRST and appends whatever the server said,
              rather than passing a fallback describeError only reaches when
              the server sent nothing usable. A 500 whose body is one word
              would otherwise render as that word alone, on a panel the reader
              opened on purpose — and "boom" does not tell an approver that
              the clock evidence is missing rather than absent. */}
          {q.error ? (
            <Notice tone="stop">
              {`Could not load the clock sessions for this week. ${serverSentence(q.error)}`}
            </Notice>
          ) : null}
          {q.data && sessions.length === 0 ? (
            /* Named, not blank. An empty region on an approval screen reads as
               a region that failed to load, and "no evidence exists" and "the
               evidence did not arrive" are not the same thing to sign off on. */
            <div className="dp-quiet">No clock sessions in this week.</div>
          ) : null}
          {sessions.map((s) => (
            <div key={s.id} data-session-row={String(s.id)} className="dp-session">
              <span className="dp-session__when">
                <span className="dp-session__day">{storedDay(s.start_time) || '—'}</span>
                <span className="dp-session__times">
                  {storedTime(s.start_time) || '—'}
                  {' – '}
                  {/* An open entry has end_time null AND duration 0. Printing
                      that as "0:00" would say the shift took no time, on the
                      screen whose own warning is that approving under an open
                      entry banks only the hours logged so far. */}
                  {s.end_time ? storedTime(s.end_time) : 'now'}
                </span>
              </span>
              <span className="dp-session__job">
                {s.job_title || (s.job_id ? `Job #${s.job_id}` : 'No job')}
                {s.start_note ? <span className="dp-session__note">{s.start_note}</span> : null}
              </span>
              <span className="dp-session__len fw-num">
                {s.end_time ? formatClock(s.duration) : 'Open'}
              </span>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}

/**
 * One line — change its hours, or take it off the sheet entirely.
 *
 * ── Why removing lives here, and why it asks twice ─────────────────────────
 *
 * Removing is the same PUT as editing: hoursModel.applyLineEdits DELETES every
 * line the array does not carry (hoursModel.js:224), so "remove" is the full
 * array minus one and there is no delete endpoint to call. That also means a
 * mis-tap is a deletion, not a draft — the line is gone from payroll the
 * moment the request lands — so it is a two-step, and the confirming step says
 * which line and how many hours come off the total.
 */
/**
 * The usual rate a line falls back to when its own is cleared, as a hint.
 *
 * Read off lineRate with the line's own amount taken away — the job's rate,
 * else the employee's — so the sentence names the same source payLines would
 * pay. `who` is the employee's first name.
 */
function usualRateHint(line, workerRate, who) {
  const usual = lineRate({ ...line, pay_amount: null }, workerRate);
  if (usual.kind !== 'hourly') return null;
  if (usual.rate === null) return `Leave it blank for the usual rate: the job's, else ${who}'s own.`;
  if (!(usual.rate > 0)) return `Blank pays nothing: there is no job rate here and ${who} has none on file.`;
  const money = `$${usual.rate.toFixed(2)}/hr`;
  return usual.source === 'job'
    ? `Leave it blank for the job rate, ${money}.`
    : `Leave it blank for ${who}'s own rate, ${money}.`;
}

function EditLine({ sheetId, entries, line, onClose, onSaved, workerName = '', who = 'the worker', workerRate = null, canPrice = false }) {
  // ── An AMOUNT line is edited as an AMOUNT, not as hours ──────────────────
  //
  // Owner, 2026-09-18: "Can only fucking remove line items with fix amounts,
  // but not EDIT@!"
  //
  // Correct, and the reason was this editor, not the server. A reimbursement,
  // bonus or adjustment is stored with `hours: 0` and paid its flat
  // `pay_amount` (isAmountLine), and the Add form below has had an amount box
  // since 2026-09-10. This sheet offered an HOURS picker and a Remove button —
  // and on such a line the hours picker moves a figure the line is not paid
  // on, so the only useful action was deleting it and typing it again.
  //
  // hoursModel.js:297 has always accepted `pay_amount` on a PUT and preserved
  // it when the field is absent, so nothing on the server changes and NO money
  // arithmetic is touched anywhere: the number the approver types is the number
  // that is stored, exactly as the Add form already does it.
  //
  // ── AND `isAmountLine` ALONE IS NOT THE TEST. See amountOfLine above ─────
  //
  // isAmountLine is true for ANY line that resolves fixed with an amount and
  // logs no hours — including a line on a fixed-price JOB whose amount comes
  // from `job_pay_amount`. Offering this editor there would have flipped the
  // line to hourly on save and paid 0 * rate = $0.00. Raised in review on #686
  // and verified link by link in the source.
  //
  // The second clause is what the owner actually asked to edit: "line items
  // with fix amounts" — a reimbursement, bonus or adjustment, which the Add
  // form writes with `pay_type: 'fixed'` and its OWN `pay_amount`
  // (sheetLines.js:202). A job-priced line keeps the hours editor it has always
  // had, unchanged.
  const isAmount = isAmountLine(line) && amountOfLine(line) != null;
  // A NUMBER, not `formatHours(line.hours)`. That was a decimal STRING —
  // "2.83" — fed to a text box, which is the shape of the whole defect
  // here. HoursField takes decimal hours and hands back decimal hours; the
  // person never sees or types either.
  const [hours, setHours] = useState(Number(line.hours) || 0);
  // A STRING, because it is bound to a text input and "0." must survive the
  // keystroke that makes it. Coerced once, on save.
  const [amount, setAmount] = useState(() => {
    const v = amountOfLine(line);
    return v == null ? '' : String(v);
  });
  const [reason, setReason] = useState(line.adjustment_reason || '');
  // ── A RATE for this line, for whoever may set pay ───────────────────────
  //
  // Owner, 2026-09-28: "admin can override at the timesheet." A line's own
  // `pay_amount` on an hourly line IS that override — paystubCalc.payLines
  // pays it over the job's rate and the employee's — and until now no screen
  // could write one. Offered only on a line that is paid by the hour (a
  // fixed-price job's line is priced by the job, and an amount line has its
  // own box above), and only to a holder of jobs.set_pay, which is what the
  // server now asks (routes/timesheets.js mayPriceLines).
  const ownRate = line.pay_type !== 'fixed' && parseFloat(line.pay_amount || 0) > 0 ? parseFloat(line.pay_amount) : null;
  const rateEditable = canPrice && !isAmount && lineRate(line, workerRate).kind === 'hourly';
  const [rate, setRate] = useState(ownRate == null ? '' : String(ownRate));
  const [rateError, setRateError] = useState(null);
  const [error, setError] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  // An amount line has no clock behind it by construction, so it can never
  // need a "why do these differ from the clock" reason.
  const needsReason = !isAmount && reasonRequired(line, Number(hours));

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await put(`/timesheets/${sheetId}`, { entries: entriesWithout(entries, line.id) });
      onSaved();
    } catch (e) {
      setError(serverSentence(e));
      setBusy(false);
      setConfirmRemove(false);
    }
  };

  const saveAmount = async () => {
    const a = Number(amount);
    if (!Number.isFinite(a) || a <= 0) { setFieldError('Enter an amount greater than zero.'); return; }
    setFieldError(null);
    setBusy(true);
    setError(null);
    try {
      // `hours` written back unchanged — an amount line is `hours: 0`, and
      // moving it would turn the row into something the pay resolver reads
      // differently.
      await put(`/timesheets/${sheetId}`, {
        entries: entriesForPut(entries, line.id, {
          hours: Number(line.hours) || 0,
          adjustment_reason: reason.trim() || null,
          pay_amount: a,
        }),
      });
      onSaved();
    } catch (e) {
      setError(serverSentence(e));
      setBusy(false);
    }
  };

  const save = async () => {
    // What is in state — snapped already if anybody touched the picker, and
    // the line's own stored figure if nobody did. A payroll figure is never
    // moved by opening an editor; see HoursField.
    //
    // Always a number: HoursField hands back partsToHours(), never a string
    // off an input. The guard stays because a NaN reaching the server is a row
    // with no hours on somebody's pay.
    const h = Number(hours);
    if (!Number.isFinite(h) || h < 0) { setFieldError('Hours must be a number, 0 or more.'); return; }
    if (needsReason && !reason.trim()) { setFieldError('Say why the hours differ from the clock.'); return; }
    // The rate goes on the wire ONLY when it changed, as a number or as null
    // (back to the usual rate). Sent unchanged it would be harmless, but an
    // absent key is how every other edit leaves a line's pay alone, and this
    // keeps the request saying exactly what the person did.
    const patch = { hours: h, adjustment_reason: reason.trim() || null };
    if (rateEditable) {
      const typed = String(rate).trim();
      const next = typed === '' ? null : Number(typed);
      if (next !== null && !(Number.isFinite(next) && next > 0)) {
        setRateError('Enter a rate above zero, or leave it blank for the usual rate.');
        return;
      }
      setRateError(null);
      if (next !== ownRate) {
        patch.pay_amount = next;
        if (next !== null) patch.pay_type = 'hourly';
      }
    }
    setFieldError(null);
    setBusy(true);
    setError(null);
    try {
      await put(`/timesheets/${sheetId}`, { entries: entriesForPut(entries, line.id, patch) });
      onSaved();
    } catch (e) {
      setError(serverSentence(e));
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      title={`${workerName || who} · ${dayShort(line.date)} · ${lineTitle(line)}`}
      onClose={onClose}
      footer={confirmRemove ? null : (
        <FwButton full size="lg" loading={busy} onClick={isAmount ? saveAmount : save}>
          {isAmount ? 'Save amount' : (rateEditable ? 'Save line' : 'Save hours')}
        </FwButton>
      )}
    >
      <div className="dp-sheet-body">
        {error ? <Notice tone="stop">{error}</Notice> : null}
        {confirmRemove ? (
          <>
            <Notice tone="stop" title="Remove this line?">
              {/* H:MM, like every other hours figure on this page. It used
                  to say "2.83 hours", which is the unit nothing else here
                  speaks. */}
              {isAmount
                ? `${moneyText(amountOfLine(line)) || 'This amount'} comes off the sheet and the line is deleted. `
                : `${hoursClock(line.hours)} comes off the sheet and it is deleted. `}
              {line.clock_hours != null
                ? 'It has a clock record behind it — "Add from clock" would bring it back.'
                : 'Nothing on the clock will bring it back.'}
            </Notice>
            {/* Stacked and full width, never side by side: two `flex: 1`
                buttons in the sheet footer squeeze at 320px, which is the
                shape of the delete-button defect in CLAUDE.md. */}
            <FwButton variant="stop" full size="lg" loading={busy} onClick={remove}>Remove line</FwButton>
            <FwButton variant="ghost" full disabled={busy} onClick={() => setConfirmRemove(false)}>Keep the line</FwButton>
          </>
        ) : (
          <>
            {/* ── A TIME, picked. Not a decimal, typed ──────────────────
                Owner, 2026-09-16, holding a sheet where a line read 2.83:
                "make sure to show a clock or something for them to pick
                hours, this way we avoid someone misstyping."

                This was `<input type=number step=0.25>` with a hint reading
                "Clock said 2.75", on a page whose grid, week total and change
                history are all H:MM. One screen, two units — and 2.83 is
                2 h 49.8 m, which is not what anybody typing it meant.
                shared/hoursPicker.js carries the full reading. */}
            {isAmount ? (
              /* The same field the Add form uses for the same number, so one
                 kind of line is typed one way wherever you meet it. */
              <FwField
                label="Amount"
                htmlFor="dp-edit-amount"
                required
                error={fieldError}
                hint={`${payCategoryLabel(payCategory(line)) || 'Adjustment'} — the flat figure this line pays.`}
              >
                <FwInput
                  id="dp-edit-amount"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(ev) => setAmount(ev.target.value)}
                />
              </FwField>
            ) : (
              <HoursField
                id="dp-edit-hours"
                value={Number(hours) || 0}
                onChange={setHours}
                clockHours={line.clock_hours}
              />
            )}
            {rateEditable ? (
              <FwField label="Rate for this line ($/hr)" htmlFor="dp-edit-rate" error={rateError} hint={usualRateHint(line, workerRate, who)}>
                <FwInput
                  id="dp-edit-rate"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={rate}
                  onChange={(ev) => setRate(ev.target.value)}
                  data-field="line-rate"
                />
              </FwField>
            ) : null}
            <FwField label="Reason" htmlFor="dp-edit-reason" required={needsReason} error={isAmount ? null : fieldError} hint={needsReason ? 'Required when the hours differ from the clock.' : undefined}>
              <FwTextarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
            </FwField>
            <FwButton variant="ghost" full icon="trash" disabled={busy} onClick={() => { setError(null); setFieldError(null); setConfirmRemove(true); }}>
              Remove this line
            </FwButton>
          </>
        )}
      </div>
    </Sheet>
  );
}

/**
 * Add a line the clock never recorded.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * A crew member forgets to clock in on a job. There is no time entry, so "Add
 * from clock" finds nothing, and until now the sheet had no other way in: the
 * screen could edit an existing line's hours and nothing else. The worker was
 * simply not paid for that job.
 *
 * ── Same save path as the hours edit ───────────────────────────────────────
 *
 * PUT /api/timesheets/:id with the FULL entries array. An entry with no `id`
 * is INSERTED by hoursModel.applyLineEdits (hoursModel.js:203); the payload is
 * built by sheetLines.entriesWithNewLine, which is where the contract is
 * written down and pinned.
 *
 * ── …or a line that is a SET AMOUNT ────────────────────────────────────────
 *
 * Added 2026-09-10. The owner: "The add a line by hand looks good, but
 * doesn't let me add a set cost. What if I want to adjust the timesheet to
 * show reinbursement, bonus, etc or other adjustment that has a set amount
 * and not hourly."
 *
 * The payload rules and every reason behind them are in sheetLines.js. What
 * this component is responsible for is that the two modes CANNOT BE
 * CONFUSED — the hours box and the amount box never exist at the same time,
 * so there is nothing to type 50 dollars into by mistake. That is not
 * fastidiousness: a wrong figure here looks like a plausible number and is
 * only found when somebody's pay is wrong.
 *
 * Nothing here computes pay. An amount goes on the wire; what it is worth is
 * decided by paystubCalc.computeFigures. CLAUDE.md rule 4.
 */
const EMPTY_DRAFT = {
  kind: 'hours', job_id: '', date: '', hours: '', custom_description: '',
  // No default. A category that arrives pre-chosen is a category nobody
  // chose, and the whole point is that the row says what somebody meant.
  pay_category: '', pay_amount: '', notes: '',
};

function AddLine({ sheetId, sheet, entries, onClose, onSaved }) {
  // Only while this sheet is open — a picker nobody opened should cost nothing.
  const jobsQ = useQuery({ queryKey: ['jobs', 'linePicker'], queryFn: () => get('/jobs'), staleTime: 60000 });
  const days = weekDates(sheet.start_date, sheet.end_date);
  const { week, rest } = jobChoices(sheet.week_jobs, jobsQ.data);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [refusal, setRefusal] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const field = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));
  const isAmount = draft.kind === 'amount';
  // Undefined until one is picked, which is what drives the hint, the note and
  // the placeholder below.
  const chosenCategory = payCategory(draft.pay_category);
  // Switching mode CLEARS the other side's fields rather than hiding them.
  // A hidden value that still rides along on the payload is how a form sends
  // something nobody can see it sending — and on this screen that is hours or
  // money nobody chose. The day and the note survive, because they mean the
  // same thing on both.
  const setKind = (e) => setDraft((d) => ({
    ...EMPTY_DRAFT,
    kind: e.target.value === 'amount' ? 'amount' : 'hours',
    date: d.date,
    notes: d.notes,
  }));

  const save = async () => {
    const why = newLineError(draft, entries);
    if (why) { setRefusal(why); return; }
    setRefusal(null);
    setBusy(true);
    setError(null);
    try {
      await put(`/timesheets/${sheetId}`, { entries: entriesWithNewLine(entries, draft) });
      onSaved();
    } catch (e) {
      setError(serverSentence(e));
      setBusy(false);
    }
  };

  return (
    <Sheet open title={`Add a line for ${(sheet && sheet.user_name) || "this worker"}`} onClose={onClose} footer={<FwButton full size="lg" loading={busy} onClick={save}>Add line</FwButton>}>
      <div className="dp-sheet-body">
        {error ? <Notice tone="stop">{error}</Notice> : null}
        {refusal ? <Notice tone="stop">{refusal}</Notice> : null}
        {/* First, because it decides what the rest of the form is. A <select>
            rather than a segmented control: it is one tap on a phone, it
            needs no width of its own, and <option> text is the only label
            that cannot be squeezed off the screen by its neighbour. */}
        <FwField
          label="What kind of line"
          htmlFor="dp-add-kind"
          hint={isAmount
            ? 'A flat sum on this sheet. It adds no hours to the week.'
            : 'Work with a job and hours behind it.'}
        >
          <FwSelect value={draft.kind} onChange={setKind}>
            <option value="hours">Hours worked</option>
            <option value="amount">A set amount — reimbursement, bonus, adjustment</option>
          </FwSelect>
        </FwField>
        <FwField label="Day" htmlFor="dp-add-date" hint="Days in this sheet's week — a line dated outside it belongs to no sheet anybody reviews.">
          <FwSelect value={draft.date} onChange={field('date')}>
            <option value="">Pick a day</option>
            {days.map((d) => <option key={d} value={d}>{dayLong(d)}</option>)}
          </FwSelect>
        </FwField>
        {isAmount ? (
          <>
            {/* ── What KIND of payment, before what it is called ───────────
                Owner, 2026-09-16: "set some default useful settings like
                Reimbursement so they clearly get marked to make things easier
                for the employee accountant."

                First of the amount fields on purpose: it changes the hint and
                the placeholder under it, and a person should pick the kind
                before writing the sentence. A <select> for the same reason the
                kind picker above is one — one tap, no width of its own, and
                <option> text cannot be squeezed off a phone screen by a
                neighbour.

                It changes NO figure (CLAUDE.md rule 4). The row pays exactly
                what it paid before this existed. */}
            <FwField
              label="What kind of payment"
              htmlFor="dp-add-category"
              required
              hint={chosenCategory ? chosenCategory.hint : 'So the books can tell a repayment from wages.'}
            >
              <FwSelect id="dp-add-category" value={draft.pay_category} onChange={field('pay_category')}>
                <option value="">Pick one</option>
                {PAY_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </FwSelect>
            </FwField>
            {/* Printed where the decision is being made rather than in a help
                page. This application computes NO tax — paystubs.deductions is
                a figure a person types — so this is guidance for whoever does
                the books, and is deliberately not worded as though the
                software acts on it. */}
            {chosenCategory && chosenCategory.note ? (
              <Notice tone="info" data-line-category-note>{chosenCategory.note}</Notice>
            ) : null}
            {/* An amount line has NO job (sheetLines.js says why), so this is
                the only thing it can be called — on this sheet, on the
                approver's screen and on the paystub. */}
            <FwField
              label="What this is for"
              htmlFor="dp-add-what"
              required
              hint={chosenCategory && chosenCategory.needsSpecific
                ? 'Say exactly what this is — "Other" on a paystub tells nobody anything.'
                : 'This is what the line is called on the paystub.'}
            >
              <FwInput
                value={draft.custom_description}
                onChange={field('custom_description')}
                placeholder={(chosenCategory && chosenCategory.placeholder) || 'Fuel reimbursement, tool allowance…'}
              />
            </FwField>
            {/* The dollar sign is IN THE LABEL, not only in a hint. This box
                and the Hours box never coexist, and the one that is on screen
                has to say which unit it wants without being read twice. */}
            <FwField
              label="Amount ($)"
              htmlFor="dp-add-amount"
              required
              hint="Paid in full, once, on this sheet. No hours are added to the week."
            >
              <FwInput type="number" inputMode="decimal" step="0.01" min="0" value={draft.pay_amount} onChange={field('pay_amount')} />
            </FwField>
          </>
        ) : (
          <>
            <FwField
              label="Job"
              htmlFor="dp-add-job"
              hint={jobsQ.isError ? 'The full job list did not load. The jobs worked this week are still listed.' : undefined}
            >
              <FwSelect value={draft.job_id} onChange={field('job_id')}>
                <option value="">Pick a job</option>
                {week.length ? (
                  <optgroup label="Worked this week">
                    {week.map((j) => <option key={j.id} value={j.id}>{j.label}</option>)}
                  </optgroup>
                ) : null}
                {rest.length ? (
                  <optgroup label="Other jobs">
                    {rest.map((j) => <option key={j.id} value={j.id}>{j.label}</option>)}
                  </optgroup>
                ) : null}
              </FwSelect>
            </FwField>
            {/* Deliberately NOT prefilled from the week's clock total: week_jobs
                counts a job across the WHOLE week, and this line is one DAY. A
                number that looks measured and is not is the worst thing to put in
                front of somebody about to approve payroll. */}
            <HoursField
              id="dp-add-hours"
              value={Number(draft.hours) || 0}
              onChange={(h) => setDraft((d) => ({ ...d, hours: h }))}
              hint="These hours are what payroll pays."
            />
          </>
        )}
        <FwField label="Note" htmlFor="dp-add-notes" hint="Optional — say why this line is here, for whoever approves it.">
          <FwTextarea rows={2} value={draft.notes} onChange={field('notes')} />
        </FwField>
      </div>
    </Sheet>
  );
}

export default function SheetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['timesheet', id], queryFn: () => get(`/timesheets/${id}`) });
  const [editing, setEditing] = useState(null);
  // Which job the reader asked about. Separate from `editing` on purpose: one
  // is "change this number", the other is "what IS this", and answering the
  // second must never put you in the first.
  const [peek, setPeek] = useState(null);
  const [adding, setAdding] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  // The server's question when the week has hours no rate reaches — its own
  // sentence and the lines it means (PUT /:id/approve, code unpriced_hours).
  const [unpricedAsk, setUnpricedAsk] = useState(null);

  const sheet = q.data;
  usePageChrome(sheet ? sheet.user_name : 'Timesheet', sheet ? `One sheet · ${weekOfLabel(sheet.start_date)}` : 'Timesheet');
  const entries = list(sheet && sheet.entries);
  const approved = !!sheet && sheet.status === 'approved';
  const phone = usePhoneLayout();
  // WHO this sheet is, in full and as a first name for sentences.
  const workerName = (sheet && sheet.user_name) || '';
  const who = firstName(workerName);
  // The employee's own rate — `grid.rate`, sent only to a reader who may see
  // rates, and 0 when the profile has none. null when withheld.
  const seenRate = sheet && sheet.grid && sheet.grid.money ? sheet.grid.rate : null;
  // A reader withheld the figure still learns whether there is ANY rate on
  // file: `worker_rate_missing`, a yes/no the server sends to every reader
  // (review of #734 — a manager approves most weeks, and the "$0.00" warning
  // never reached them). No rate on file is the same fact as a rate of 0, so
  // it is read as one; a rate merely withheld stays null, and unknown.
  const ratesVisible = seenRate !== null;
  const workerRate = ratesVisible ? seenRate : (sheet && sheet.worker_rate_missing === true ? 0 : null);
  // POST/PUT /api/timesheets asks jobs.set_pay before a line's rate moves
  // (routes/timesheets.js mayPriceLines). The same question, so the field is
  // never offered to somebody the server would refuse.
  const canPrice = useCan('jobs', 'set_pay');
  // Hourly lines with NO rate anywhere, which pay $0.00. Known when the
  // reader sees the employee's rate, or is told there is none on file; for a
  // reader told neither, a line with no rate of its own may well be on the
  // employee's, and that is not ours to guess.
  const unpriced = workerRate === null ? [] : entries.filter((e) => {
    const r = lineRate(e, workerRate);
    return r.kind === 'hourly' && r.rate === 0 && Number(e.hours) > 0;
  });
  const pill = sheet ? sheetPill(sheet) : null;

  // The week's pay, or null — the server's own figure, withheld entirely for a
  // reader without `workers.read_sensitive` (a manager approving is exactly
  // that). `moneyText` answers null for a null, so the footer draws nothing
  // rather than "$0.00", which would be a WRONG number on the control that
  // turns hours into pay.
  const sheetPay = moneyText(sheet && sheet.grid && sheet.grid.total ? sheet.grid.total.pay : null);

  // What each LINE is worth, by its id — a lookup into the server's grid, not
  // a calculation. Empty when the reader may not see money, which is what
  // makes every `moneyText` below answer null and draw nothing.
  const payByLine = new Map();
  for (const row of list(sheet && sheet.grid && sheet.grid.rows)) {
    for (const cell of Object.values(row.cells || {})) payByLine.set(cell.id, cell.pay);
  }

  /**
   * One line, exactly as it was before the grid existed.
   *
   * Handed to TimesheetGrid, which decides only WHERE it goes — inside the day
   * it happened on. Everything a line says about itself is unchanged: the
   * provenance badge, the adjustment reason and who made it, what the clock
   * said, and the collapsed history of every earlier change.
   *
   * `value` is the one addition, and it comes off the SERVER's grid rather
   * than being worked out here: `linePriceLabel` says what a line is priced AT
   * ("$32.50/hr", "Fixed $500.00") and could never say what it is WORTH,
   * because that needs the worker's rate and no timesheet response carried
   * one. Now one does, so the row an approver is reading finally carries the
   * figure they are approving. Multiplying hours by a rate here instead would
   * be #202 exactly (CLAUDE.md rule 4).
   */
  const renderLine = (e) => {
    const value = moneyText(payByLine.get(e.id));
    const rate = rateOf(e, workerRate, who);
    return (
      <div className="dp-line" data-entry-id={e.id} data-adjusted={e.adjusted ? 'true' : 'false'}>
        {/* ── The name opens the job; the pencil opens the hours ──────────
            Owner, 2026-09-18, two reports about this one row:

              "WTF did I say about the jobs having a clickable link to open a
               modal with details to review inside the timesheet??!!! what the
               fuck is that stupid fucking 'what is <job>' Looks fucking stupid
               and cheap"

              "How the fuck does admin edit a timesheet?? fucking basics"

            Both were true, and they had the same cause. MEASURED on /approve/1
            at 390x844 before this change: five lines, five cards that were
            tappable-to-edit, and the word "edit" appearing NOWHERE on the
            screen — so the edit action existed and nothing said so. Meanwhile
            the only visible affordance on each line was a full-width ghost
            button reading "What is Grid mow (ui-check)?", wrapping to two
            lines, five times down the page.

            That button was there because `trailing` renders inside JobCard's
            own <button> when the card is tappable, so it could not live in the
            card. JobCard takes `onTitleClick` now: the card stops being a
            button, the NAME becomes the link into JobPeek, and `trailing` is
            free to carry a real Edit control. Two things to press, each
            labelled with what it does. */}
        {/* NO `time` prop below. It was `dayShort(e.date)` — "Mon 14" — inside
            a day group whose own heading already reads "Mon 14 Sep", so it was
            the same fact twice, and it cost a whole column.

            MEASURED at 320px once the pencil arrived: the card is
            `time | text | trailing`, and the text column fell to 104px, where
            an amount line's meta ("Correction to an earlier week Fixed $25.00")
            wrapped to FIVE lines of about one word each. ui-check's own "no
            text wrapping one word per line" invariant caught it — a regression
            I introduced, on the screen I was fixing.

            Dropping the duplicated day gives back ~72px, more than the pencil's
            44px plus its gap costs. `.fw-jcard__time:empty` already removes the
            column when there is nothing in it, so this is one prop rather than
            a new layout. DayGroups is the ONLY caller of renderLine and it
            always draws the heading, so no date is lost. */}
        <JobCard
          title={lineTitle(e)}
          meta={lineMeta(e, value, rate)}
          tone={e.adjusted ? 'warn' : 'neutral'}
          onTitleClick={e.job_id != null ? () => setPeek(e) : undefined}
          titleLabel={e.job_id != null ? `Open ${e.job_title || 'this job'}` : undefined}
          // NEVER `onClick` as well. An AMOUNT line has no job by construction
          // (sheetLines.js), so it gets no title link — and if the whole card
          // were also tappable-to-edit on those rows, the Edit button below
          // would be a <button> inside a <button>, which is invalid and does
          // not fire. That is the same trap that pushed "What is <job>?" out of
          // the card in the first place. The pencil is the ONE edit affordance
          // on every kind of line, which is also the answer to "how does admin
          // edit a timesheet".
          badges={lineBadges(e)}
          trailing={!approved ? (
            <FwButton
              variant="quiet"
              icon="edit"
              className="dp-line__edit"
              data-line-edit={e.id}
              aria-label={`Edit ${lineTitle(e)}`}
              title="Edit this line"
              onClick={() => setEditing(e)}
            />
          ) : undefined}
        />
        {/* The job's own dates get a row of their own rather than a share of
            the card's meta line.
            MEASURED: inside the meta they had 159px at 320px — the card's text
            column is `flex: 1 1 auto` after the time column and the price —
            and "CREATED Wed 16 12:45 PM" wrapped over FIVE lines, one word
            each. ui-check caught it; every jsdom test above was green, because
            jsdom lays nothing out. Full width here, and it fits. */}
        {lineWhen(e)}
        {e.adjusted ? (
          <div className="dp-line__note">
            {e.adjustment_reason ? <span>{e.adjustment_reason}{e.adjusted_by_name ? ` — ${e.adjusted_by_name}` : ''}. </span> : null}
            {/* "Clock said 2:45", not "2.75". Same number; the second one
                is in a unit no other figure on this screen uses, and the
                owner read it as nonsense — correctly. */}
            {e.clock_hours != null ? <span className="dp-line__note-clock">Clock said <span className="fw-num">{hoursClock(e.clock_hours)}</span></span> : null}
          </div>
        ) : null}
        {/* Everything that came BEFORE the reason above — the changes the
            single adjustment_reason column could not keep. */}
        <LineHistory line={e} />
      </div>
    );
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['timesheet', id] });
    qc.invalidateQueries({ queryKey: ['dispatch', 'approve'] });
  };

  useEffect(() => {
    if (!done) return undefined;
    // Only when there is nothing to READ. If the approval left jobs open, the
    // notice below lists them and answers "why is this still on my board
    // after payroll?" before anyone asks — and 2.5 seconds is not long enough
    // to read a list. So the screen stays put and the approver leaves when
    // they are done. With nothing left open it returns to the queue exactly as
    // it always has (#693).
    if (done.leftOpen && done.leftOpen.length) return undefined;
    const t = setTimeout(() => navigate('/approve'), 2500);
    return () => clearTimeout(t);
  }, [done, navigate]);

  // `allowUnpriced` is the approver's yes to the server's question below: it
  // is only ever sent from that sheet, never on the first press.
  const decide = async (status, { allowUnpriced = false } = {}) => {
    setBusy(true);
    setError(null);
    try {
      const body = status === 'rejected' ? { status, notes: note.trim() } : { status };
      if (allowUnpriced) body.allow_unpriced = true;
      const res = await put(`/timesheets/${id}/approve`, body);
      refresh();
      setBusy(false);
      setUnpricedAsk(null);
      if (status === 'approved') {
        setDone({
          paystubId: res && res.paystub_id,
          leftOpen: Array.isArray(res && res.left_open_jobs) ? res.left_open_jobs : [],
        });
      }
      else { setRejecting(false); navigate('/approve'); }
    } catch (e) {
      setBusy(false);
      setRejecting(false);
      // Hours no rate reaches: the server asks rather than paying them $0.00
      // quietly (owner, 2026-09-28: "rate displays as 0.00/hr"). Asked here,
      // in the server's words, with the lines it named.
      if (e && e.status === 409 && e.body && e.body.code === 'unpriced_hours' && !allowUnpriced) {
        setUnpricedAsk(e.body);
        return;
      }
      setUnpricedAsk(null);
      setError(serverSentence(e));
    }
  };

  const addFromClock = async () => {
    if (!sheet) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await post('/timesheets/from-clock', { user_id: sheet.user_id, week_start: sheet.start_date });
      refresh();
      const n = Number(res && res.added) || 0;
      setInfo(n ? `Added ${n} ${n === 1 ? 'line' : 'lines'} from the clock.` : 'Nothing on the clock that is not already on the sheet.');
    } catch (e) {
      setError(serverSentence(e));
    }
    setBusy(false);
  };

  return (
    <>
      {q.isPending ? <Skeleton lines={6} /> : null}
      {q.error ? <Notice tone="stop" className="dp-notice">{describeError(q.error)}</Notice> : null}
      {sheet ? (
        <div className="dp-stack">
          {/* ── WHOSE sheet this is, on a desktop ─────────────────────────
              Owner, 2026-09-28: "make sure when editing timesheets, it
              clearly shows the name of the employee."

              MEASURED at 1440x900 before this: no heading at all, and the
              full name nowhere on the page. usePageChrome above hands the
              name to the PHONE header only (AppShell passes `title` to
              PhoneShell alone; shell/chrome.js: "on desktop the screen
              renders its own PageHeader"), and this screen never did — so
              the one clue was a first name inside the blue notice, which an
              approved sheet does not even have. On a phone the shell's
              header already says it, so this is not drawn twice. */}
          {!phone ? (
            <header className="dp-sheet-head" data-sheet-head>
              <div className="dp-sheet-head__eyebrow">Timesheet · {weekOfLabel(sheet.start_date)}</div>
              <div className="dp-sheet-head__row">
                <h1 className="dp-sheet-head__name">{workerName || 'Unnamed worker'}</h1>
                {pill ? <Pill tone={pill.tone} data-sheet-status={sheet.status || 'draft'}>{pill.label}</Pill> : null}
              </div>
            </header>
          ) : null}
          {error ? <Notice tone="stop">{error}</Notice> : null}
          {done ? (
            <Notice tone="ok" title="Approved">
              {done.paystubId ? `Paystub #${done.paystubId} generated.` : 'No paystub was generated.'}
            </Notice>
          ) : null}
          {done && done.leftOpen && done.leftOpen.length ? (
            /* Why these are still on the board after payroll: approval only
               archives work somebody has marked finished (#691). Named, so the
               approver can finish them or leave them running on purpose. */
            <Notice tone="info" title={`${done.leftOpen.length === 1 ? 'One job was' : `${done.leftOpen.length} jobs were`} left open`}>
              <p className="dp-leftopen__why">
                Nobody has marked {done.leftOpen.length === 1 ? 'it' : 'them'} finished, so approving this week did not
                archive {done.leftOpen.length === 1 ? 'it' : 'them'}. {done.leftOpen.length === 1 ? 'It stays' : 'They stay'} on
                the board and can take next week&apos;s hours.
              </p>
              <ul className="dp-leftopen__list">
                {done.leftOpen.map((j) => <li key={j.id}>{j.title}</li>)}
              </ul>
              {/* The screen stays put so this can be read; this is the way on,
                  rather than leaving the shell nav as the only exit. */}
              <FwButton variant="ghost" icon="arrowLeft" onClick={() => navigate('/approve')} data-leftopen-back>
                Back to approvals
              </FwButton>
            </Notice>
          ) : null}
          {info ? <Notice tone="ok" onDismiss={() => setInfo(null)}>{info}</Notice> : null}
          {!approved ? (
            <Notice tone="info" icon={null}>
              Open until approved — {who} can keep adding days. One sheet per week.
            </Notice>
          ) : (
            <Notice tone="ok" data-processed={sheet.paystub && sheet.paystub.id ? (sheet.paystub.status || 'pending') : 'none'}>
              {/* Whether it went through, not only that it was approved: the
                  paystub's own state, which this response has always carried
                  and nothing printed. */}
              Approved
              {sheet.paystub && sheet.paystub.id
                ? ` · Paystub #${sheet.paystub.id}, ${sheet.paystub.status === 'paid' ? 'paid' : 'not paid yet'}`
                : ' · No paystub made from it'}
              . Reject it to reopen the week.
            </Notice>
          )}

          {/* ── A line that pays $0.00, said before it is approved ─────────
              Owner, 2026-09-28: "rate displays as 0.00/hr". MEASURED on a
              scratch server: a worker with no hourly rate on file, five
              hours on a job with no rate — the sheet showed $0.00 as quietly
              as any other figure, Approve went through, and the paystub read
              "$0.00/hr" and $0.00 gross. Nothing anywhere said the week was
              about to pay nothing. */}
          {unpriced.length ? (
            <Notice tone="warn" title={`${unpriced.length === 1 ? 'One line pays' : `${unpriced.length} lines pay`} nothing`} data-no-rate={unpriced.length}>
              {who} has no hourly rate on file, and {unpriced.length === 1 ? 'this line has' : 'these lines have'} no job rate
              or rate of {unpriced.length === 1 ? 'its' : 'their'} own, so {unpriced.length === 1 ? 'it pays' : 'they pay'} $0.00.
              {' '}{ratesVisible ? 'Set' : 'Ask an admin to set'} {who}&apos;s rate under <Link to="/crew" className="dp-link">Crew</Link>
              {canPrice && !approved ? ', or set a rate on the line with its edit button.' : '.'}
            </Notice>
          ) : null}

          {/* ── The two ways a line gets onto this sheet, ABOVE everything
                it would put there ────────────────────────────────────────
              The owner, 2026-09-10: "time sheets have to scroll all the way
              to the bottom to see the add from clock (Which doesn't tell me
              wtf it exactly does)". This block used to sit after the week's
              job cards, the clock-session panel and every line — so the
              emptier the sheet, the further somebody scrolled past nothing
              to find the control that fills it. It is the first thing under
              the status now, at any sheet length.
              The label names the OUTCOME, not the source, and the sentence
              under it is read off routes/timesheets.js POST /from-clock and
              hoursModel.buildOrRefreshFromClock — one line per job per day,
              finished entries only, nothing already on the sheet touched.
              SheetDetail.test.js reads that source and fails if the sentence
              stops being true. */}
          {/* ── …and it is ONE ROW now, because it was 171px of it ───────
              Owner, 2026-09-18: "Have to scroll down and fucking look for the
              timesheet line item".

              MEASURED at 390x844: the first `.dp-line` sat at y=975 — 131px
              BELOW the fold — so no part of the thing being approved was on
              screen at load. This block was 171px of what put it there: two
              full-width buttons stacked with a two-line paragraph between them.

              Both complaints are real and they pull opposite ways, so neither
              is answered by MOVING this block: it is up here because of the
              earlier one ("have to scroll all the way to the bottom to see the
              add from clock"), and putting it back would simply re-break that.
              The fix is for it to be SMALL. Two buttons on one row, the
              sentence on one line under them: 171px -> 115px.

              `wrap` on both, because without it the labels TRUNCATED to
              "ADD CLO…" and "ADD BY H…" at 390px — `.fw-btn__label` ellipsizes
              and only `--full` and `--wrap` opt out. A truncated label is
              unreadable at any height. */}
          {!approved ? (
            <div className="dp-actions dp-actions--row">
              <FwButton variant="ghost" wrap icon="clock" loading={busy} onClick={addFromClock}>
                Add clock time
              </FwButton>
              {/* The other half of that button. It can only find what the
                  clock recorded; a forgotten clock-in leaves nothing to find,
                  and that hour was going unpaid because the screen offered no
                  way to type it. */}
              <FwButton variant="ghost" wrap icon="plus" disabled={busy} onClick={() => setAdding(true)}>Add by hand</FwButton>
              {/* Still says what the button DOES — that sentence exists because
                  the owner said the control "doesn't tell me wtf it exactly
                  does", and SheetDetail.test.js reads routes/timesheets.js to
                  check it is still true. On its own row, so the buttons above
                  are never narrowed by it. */}
              <div className="dp-quiet" data-clock-action-hint>
                One line per job per day, from finished clock entries this week. Pressing it twice is safe — anything already on the sheet is left alone.
              </div>
            </div>
          ) : null}

          {/* ── The sheet, as a timesheet ───────────────────────────────
              The owner, 2026-09-10: "just shows a cluster fuck of jobs and
              nothing that makes sense!! what am I approving, how can I tell
              what is what?!! what if a job needs to be removed .. where do I
              see the hours, the amount in dollars".

              What was here was `Lines · 9` over a flat column of nine cards —
              the same column at 1440px as at 320px. TimesheetGrid puts the
              week's total first, both edges of the grid on the screen, and
              the lines inside the day they happened on. It renders each line
              with the function below, so the badges, the adjustment reason,
              the clock figure and the change history are unchanged: the
              grouping moved, the record did not.

              It sits ABOVE the week panel and the clock sessions now.
              Those are EVIDENCE — "is anything missing", "when was this
              worked" — and the thing being approved should not be underneath
              the argument for it. */}
          <TimesheetGrid
            grid={sheet.grid}
            entries={entries}
            renderLine={renderLine}
            onPickLine={approved ? ((e) => (e && e.job_id != null ? setPeek(e) : undefined)) : ((e) => setEditing(e))}
            onPeekJob={(row) => setPeek(entries.find((e) => e.job_id === row.job_id) || null)}
          />
          {entries.length === 0 ? <div className="dp-quiet">No lines yet. Add them from the clock, or by hand.</div> : null}

          <WeekJobs sheet={sheet} entries={entries} />

          {/* The raw clock under those totals. On an APPROVED sheet too —
              that is exactly when somebody asks how a figure was reached. */}
          <ClockSessions sheetId={id} />

          <div className="dp-footer">
            {/* What is being authorised, next to the button that authorises
                it. The corner at the top of the grid carries the same pair,
                and this is the copy that is on screen when the thumb is on
                Approve — a total somebody has to scroll back up to check is a
                total they approve without checking.

                Both figures are the SERVER's: `total_hours` off the sheet and
                `grid.total.pay` off timesheetGrid, which is computeFigures.
                Nothing here adds anything up. */}
            {/* WHOSE week the thumb is about to approve — the footer is the
                part of the screen still in view when the header has scrolled
                away (owner, 2026-09-28: "clearly shows the name of the
                employee"). The pay beside it only for a reader who may see
                it, as before. */}
            {!approved && !done ? (
              <div className="dp-footer__sum" data-approve-sum>
                <span className="dp-footer__who" data-approve-who>{workerName}</span>
                <span className="fw-num">{hoursClock(sheet.total_hours)}</span>
                {sheetPay ? <span className="dp-footer__pay fw-num">{sheetPay}</span> : null}
              </div>
            ) : null}
            {!approved && !done ? (
              <FwButton variant="go" size="lg" full loading={busy} onClick={() => decide('approved')}>
                Approve · <span className="fw-num">{hoursClock(sheet.total_hours)}</span>
              </FwButton>
            ) : null}
            {!done ? (
              <FwButton variant="ghost" className="dp-btn-stop" full disabled={busy} onClick={() => { setNote(''); setRejecting(true); }}>Reject</FwButton>
            ) : null}
          </div>
        </div>
      ) : null}

      {editing ? (
        <EditLine
          sheetId={id}
          entries={entries}
          line={editing}
          workerName={workerName}
          who={who}
          workerRate={workerRate}
          canPrice={canPrice}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      ) : null}

      {/* Reads the row it was given — sheetLines already joins jobs, clients
          and properties, so this opens instantly and cannot fail on a bad
          connection in a yard. */}
      {peek ? <JobPeek job={jobFromRow(peek)} onClose={() => setPeek(null)} /> : null}

      {adding && sheet ? (
        <AddLine sheetId={id} sheet={sheet} entries={entries} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); refresh(); }} />
      ) : null}

      {/* The server's question, not a guess made here: it knows the rate this
          reader may not see. Two answers, and the safe one is the plain one. */}
      <Sheet
        open={!!unpricedAsk}
        title="Approve at $0.00?"
        onClose={() => setUnpricedAsk(null)}
        footer={(
          <>
            <FwButton variant="ghost" full size="lg" disabled={busy} onClick={() => setUnpricedAsk(null)} data-action="set-rate-first">Set the rate first</FwButton>
            <FwButton variant="stop" full size="lg" loading={busy} onClick={() => decide('approved', { allowUnpriced: true })} data-action="approve-unpriced">Approve at $0.00</FwButton>
          </>
        )}
      >
        {unpricedAsk ? (
          <div className="dp-sheet-body" data-unpriced-ask>
            <p>{unpricedAsk.error}</p>
            {list(unpricedAsk.lines).length ? (
              <ul className="dp-unpriced">
                {list(unpricedAsk.lines).map((l) => (
                  <li key={l.id || `${l.date}-${l.title}`}>
                    <span className="dp-unpriced__what">{l.date ? `${dayShort(l.date)} · ` : ''}{l.title}</span>
                    <span className="fw-num">{hoursClock(l.hours)}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            <p className="dp-unpriced__hint" data-unpriced-fix>{unpricedFix(canPrice, ratesVisible, who)}</p>
          </div>
        ) : null}
      </Sheet>

      <Sheet
        open={rejecting}
        title={`Reject ${who}'s week`}
        onClose={() => setRejecting(false)}
        footer={<FwButton variant="stop" full size="lg" loading={busy} disabled={!note.trim()} onClick={() => decide('rejected')}>Reject</FwButton>}
      >
        <div className="dp-sheet-body">
          <FwField label="Note to the worker" htmlFor="dp-reject-note" required hint="Rejecting reopens the week for editing.">
            <FwTextarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          </FwField>
        </div>
      </Sheet>
    </>
  );
}
